import { useState } from "react";
import { API_BASE } from "../api/fetchAppData";
import type { AppDataState } from "../api/useAppData";

export default function DebugPage({ state }: { state: AppDataState }) {
  const { data, refetch } = state;
  const [refreshing, setRefreshing] = useState(false);
  const [lastResult, setLastResult] = useState<string | null>(null);
  const [simToken, setSimToken] = useState("");

  async function triggerRefresh() {
    setRefreshing(true);
    setLastResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/refresh`, {
        method: "POST",
        headers: simToken ? { "X-Sim-Token": simToken } : undefined,
      });
      const body = await res.text();
      setLastResult(body);
      await refetch();
    } catch (e) {
      setLastResult(String(e));
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <>
      <h1>Debug</h1>
      <div className="card">
        <input
          type="password"
          placeholder="Sim admin token"
          value={simToken}
          onChange={(e) => setSimToken(e.target.value)}
          autoComplete="off"
          style={{ marginRight: 10 }}
        />
        <button type="button" onClick={triggerRefresh} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "Force refresh"}
        </button>
        {lastResult ? (
          <pre className="debug-pre" style={{ marginTop: 10 }}>
            {lastResult}
          </pre>
        ) : null}
      </div>

      {data?.debug ? (
        <>
          <h2 className="section">App data debug</h2>
          <DebugTable rows={debugRows(data)} />
          {data.debug.unmappedTeams.length > 0 ? (
            <>
              <h2 className="section">Unmapped team names</h2>
              <div className="card">
                <ul>
                  {data.debug.unmappedTeams.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </div>
            </>
          ) : null}
          {data.debug.unmappedStages.length > 0 ? (
            <>
              <h2 className="section">Unmapped stages</h2>
              <div className="card">
                <ul>
                  {data.debug.unmappedStages.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ul>
              </div>
            </>
          ) : null}
          <h2 className="section">Raw API team names</h2>
          <div className="card">
            <ul>
              {data.debug.rawApiTeamNames.map((t) => (
                <li key={t}>{t}</li>
              ))}
            </ul>
          </div>
        </>
      ) : (
        <p>No data yet.</p>
      )}
    </>
  );
}

function debugRows(data: NonNullable<AppDataState["data"]>) {
  return [
    ["API last fetched", fmt(data.debug.apiLastFetched)],
    ["KV last written", fmt(data.debug.kvLastWritten)],
    ["Last successful refresh", fmt(data.debug.lastSuccessfulRefresh)],
    ["Fixtures loaded", String(data.debug.fixtureCount)],
    ["Live fixtures", String(data.debug.liveFixtureCount)],
    ["Finished fixtures", String(data.debug.finishedFixtureCount)],
    ["Teams mapped", String(data.debug.mappedTeamCount)],
    ["Data source status", data.debug.dataSourceStatus],
    ["Latest API error", data.debug.latestApiError ?? "—"],
    ["Retry-After until", fmt(data.debug.retryAfterUntil ?? null)],
  ] satisfies [string, string][];
}

function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function DebugTable({ rows }: { rows: [string, string][] }) {
  return (
    <div className="card">
      <table className="standings-table">
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <td className="team-cell">{label}</td>
              <td>{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
